/**
 * Arabic UI catalog for HR, settings, administration, security, licensing,
 * collaboration, help, and organization-scoped AI surfaces.
 *
 * Dynamic replacements deliberately interpolate captures unchanged so names,
 * user-entered text, IDs, document numbers, emails, URLs, dates, amounts, and
 * codes are never translated or reformatted by this catalog.
 */
export const hrSettingsAdminTranslations: Record<string, string> = {
  // Shared actions, labels, states, and accessibility text
  'Settings': 'الإعدادات',
  'Save': 'حفظ',
  'Save Changes': 'حفظ التغييرات',
  'Save Settings': 'حفظ الإعدادات',
  'Saving...': 'جاري الحفظ...',
  'Loading...': 'جاري التحميل...',
  'Loading settings...': 'جاري تحميل الإعدادات...',
  'Cancel': 'إلغاء',
  'Close': 'إغلاق',
  'Done': 'تم',
  'Back': 'رجوع',
  'Continue': 'متابعة',
  'Remove': 'إزالة',
  'Delete': 'حذف',
  'Edit': 'تعديل',
  'Update': 'تحديث',
  'Upload': 'رفع',
  'Clear': 'مسح',
  'Start': 'بدء',
  'Submit': 'إرسال',
  'Approve': 'اعتماد',
  'Reject': 'رفض',
  'Reset': 'إعادة ضبط',
  'Search': 'بحث',
  'Name': 'الاسم',
  'Full Name': 'الاسم الكامل',
  'Full Name *': 'الاسم الكامل *',
  'Email': 'البريد الإلكتروني',
  'Email Address': 'البريد الإلكتروني',
  'Email Address *': 'البريد الإلكتروني *',
  'Description': 'الوصف',
  'Status': 'الحالة',
  'Status:': 'الحالة:',
  'Date': 'التاريخ',
  'Due Date': 'تاريخ الاستحقاق',
  'Due Date:': 'تاريخ الاستحقاق:',
  'Reference': 'المرجع',
  'Amount': 'المبلغ',
  'Total': 'الإجمالي',
  'Currency': 'العملة',
  'Category': 'الفئة',
  'Department': 'القسم',
  'Role': 'الدور',
  'Permissions': 'الصلاحيات',
  'Actions': 'الإجراءات',
  'Active': 'نشط',
  'Inactive': 'غير نشط',
  'Activated': 'مفعّلة',
  'Archived': 'مؤرشف',
  'Available': 'متاح',
  'Pending': 'قيد الانتظار',
  'Approved': 'معتمد',
  'Rejected': 'مرفوض',
  'Completed': 'مكتمل',
  'Draft': 'مسودة',
  'Posted': 'مرحّل',
  'Paid': 'مدفوع',
  'Suspended': 'موقوف',
  'On Hold': 'معلّق',
  'Enabled': 'مفعّل',
  'Disabled': 'معطّل',
  'Unknown': 'غير معروف',
  'Other': 'أخرى',
  'None': 'لا شيء',
  'Default': 'افتراضي',
  'Standard': 'قياسي',
  'Professional': 'احترافي',
  'Modern': 'عصري',
  'Minimal': 'بسيط',
  'Small': 'صغير',
  'Medium': 'متوسط',
  'Large': 'كبير',
  'Monthly': 'شهري',
  'Weekly': 'أسبوعي',
  'Bi-weekly': 'كل أسبوعين',
  'Male': 'ذكر',
  'Female': 'أنثى',
  'All Modules': 'جميع الوحدات',
  'All Actions': 'جميع الإجراءات',
  'Select Account...': 'اختر حسابًا...',
  'Select account...': 'اختر حسابًا...',
  'No results found': 'لم يتم العثور على نتائج',
  'No users found': 'لم يتم العثور على مستخدمين',
  'Sign Out': 'تسجيل الخروج',
  'Hide password': 'إخفاء كلمة المرور',
  'Show password': 'إظهار كلمة المرور',

  // SettingsView — organization profile and branding
  'Manage your organization profile and staff access.': 'إدارة ملف المؤسسة وصلاحيات وصول الموظفين.',
  'Add Staff': 'إضافة موظف',
  'Organization': 'المؤسسة',
  'Default Accounts': 'الحسابات الافتراضية',
  'Invoice Layouts': 'تخطيطات الفواتير',
  'Staff': 'الموظفون',
  'Catalog Landing Page': 'الصفحة الرئيسية للكتالوج',
  'Access Rights': 'صلاحيات الوصول',
  'Basic Information': 'المعلومات الأساسية',
  'General details about your organization': 'البيانات العامة لمؤسستك',
  'Organization Logo': 'شعار المؤسسة',
  'Logo': 'الشعار',
  'Click box to upload logo': 'انقر داخل المربع لرفع الشعار',
  'Recommended: Square, PNG or JPG (Max 5MB)': 'موصى به: صورة مربعة بصيغة PNG أو JPG (بحد أقصى 5 ميجابايت)',
  'Remove Logo': 'إزالة الشعار',
  'Display Name *': 'اسم العرض *',
  'Organization Display Name': 'اسم عرض المؤسسة',
  'Legal Name': 'الاسم القانوني',
  'Legal Entity Name': 'اسم الكيان القانوني',
  'Industry': 'القطاع',
  'e.g. Technology, Retail': 'مثال: التكنولوجيا أو تجارة التجزئة',
  'Business Structure': 'الهيكل القانوني للنشاط',
  'Select Structure...': 'اختر الهيكل...',
  'Sole Proprietor': 'منشأة فردية',
  'Partnership': 'شركة أشخاص',
  'Corporation': 'شركة مساهمة',
  'Non-Profit': 'مؤسسة غير ربحية',
  'Trust': 'صندوق ائتماني',
  'Limited Liability Company (LLC)': 'شركة ذات مسؤولية محدودة (LLC)',
  'Business Reg. Number': 'رقم السجل التجاري',
  'Reg # / Tax ID': 'رقم السجل / الرقم الضريبي',
  'Tax Number (VAT/GST)': 'الرقم الضريبي (VAT/GST)',
  'Tax Registration #': 'رقم التسجيل الضريبي',
  'Organization Description': 'وصف المؤسسة',
  'Brief description of your business...': 'وصف موجز لنشاطك...',
  'Contact Information': 'معلومات الاتصال',
  'How customers and staff can reach you': 'وسائل تواصل العملاء والموظفين معك',
  'Mailing Address': 'العنوان البريدي',
  'Full business address...': 'العنوان الكامل للنشاط...',
  'Country': 'الدولة',
  'Select Country...': 'اختر الدولة...',
  'City': 'المدينة',
  'Select City...': 'اختر المدينة...',
  'Enter City': 'أدخل المدينة',
  'Attention Message': 'رسالة عناية',
  'e.g. Accounts Payable Department': 'مثال: قسم الحسابات الدائنة',
  'Phone Numbers': 'أرقام الهاتف',
  'Add Phone': 'إضافة هاتف',
  'Phone Number': 'رقم الهاتف',
  'Fax': 'الفاكس',
  'Fax Number': 'رقم الفاكس',
  'Website': 'الموقع الإلكتروني',
  'Social Media Profiles': 'حسابات التواصل الاجتماعي',
  'Facebook': 'فيسبوك',
  'Twitter / X': 'تويتر / X',
  'LinkedIn': 'لينكدإن',
  'Currency Settings': 'إعدادات العملة',
  'Configure default currency and allowed currencies for your organization': 'تحديد العملة الافتراضية والعملات المسموح بها للمؤسسة',
  'Default Currency *': 'العملة الافتراضية *',
  'Primary currency for your organization (Only one allowed)': 'العملة الأساسية للمؤسسة (يُسمح بعملة واحدة فقط)',
  'Default Payment Terms (Days)': 'شروط السداد الافتراضية (بالأيام)',
  'Number of days for payment terms': 'عدد أيام مهلة السداد',
  'Allowed Currencies': 'العملات المسموح بها',
  'Select a currency to add...': 'اختر عملة لإضافتها...',
  'Select one or more currencies allowed for your organization': 'اختر عملة واحدة أو أكثر مسموحًا بها للمؤسسة',
  'Live Exchange Rates': 'أسعار الصرف المباشرة',
  'Updated:': 'آخر تحديث:',
  'Refresh rates': 'تحديث أسعار الصرف',
  'Rates are updated automatically every 15 minutes.': 'تُحدّث الأسعار تلقائيًا كل 15 دقيقة.',
  'Sales Invoice Settings': 'إعدادات فواتير المبيعات',
  'Customize sales invoice numbering (assigned when invoice is approved)': 'تخصيص ترقيم فواتير المبيعات (يُعيّن عند اعتماد الفاتورة)',
  'Quote Prefix *': 'بادئة عرض السعر *',
  'Next Quote Number *': 'رقم عرض السعر التالي *',
  'Reset to 1': 'إعادة الضبط إلى 1',
  'Invoice Prefix *': 'بادئة الفاتورة *',
  'Next Number *': 'الرقم التالي *',
  'Credit Note Prefix *': 'بادئة الإشعار الدائن *',
  'Default Branding Theme': 'سمة الهوية البصرية الافتراضية',
  'Purchase Settings': 'إعدادات المشتريات',
  'Customize purchase order and bill numbering': 'تخصيص ترقيم أوامر الشراء وفواتير المشتريات',
  'PO Prefix *': 'بادئة أمر الشراء *',
  'Bill Prefix *': 'بادئة فاتورة المشتريات *',
  'Additional Prefixes': 'بادئات إضافية',
  'Manage additional transaction numbering prefixes': 'إدارة بادئات ترقيم المعاملات الإضافية',
  'Fixed Assets Prefix *': 'بادئة الأصول الثابتة *',
  'Manual Journal Prefix *': 'بادئة قيد اليومية اليدوي *',
  'Map system processes to specific accounts in your Chart of Accounts': 'ربط عمليات النظام بحسابات محددة في دليل الحسابات',
  'Sales Revenue Account': 'حساب إيرادات المبيعات',
  'Sales Returns Account': 'حساب مردودات المبيعات',
  'Accounts Receivable': 'الحسابات المدينة',
  'Accounts Payable': 'الحسابات الدائنة',
  'Inventory Account': 'حساب المخزون',
  'COGS Account': 'حساب تكلفة البضاعة المباعة',
  'VAT Input Account': 'حساب ضريبة المدخلات',
  'VAT Output Account': 'حساب ضريبة المخرجات',
  'VAT Settlement Account': 'حساب تسوية ضريبة القيمة المضافة',
  'Default Bank/Cash Account': 'حساب البنك/النقدية الافتراضي',
  'Purchase Account': 'حساب المشتريات',
  'Purchase Returns Account': 'حساب مردودات المشتريات',
  'Discount Given Account': 'حساب الخصم الممنوح',
  'Discount Received Account': 'حساب الخصم المكتسب',
  'Theme Library': 'مكتبة السمات',
  'Module Layouts': 'تخطيطات الوحدات',
  'Quotation': 'عرض السعر',
  'Sales Invoice': 'فاتورة المبيعات',
  'Delivery Note': 'إذن التسليم',
  'Purchase Order': 'أمر الشراء',
  'Bill': 'فاتورة مشتريات',
  'Global branding themes that can be applied to any module.': 'سمات هوية بصرية عامة يمكن تطبيقها على أي وحدة.',
  'Create Theme': 'إنشاء سمة',
  'No branding themes created yet.': 'لم تُنشأ أي سمات للهوية البصرية بعد.',
  'Layout': 'التخطيط',
  'Customize how': 'خصّص كيفية ظهور',
  'print-outs appear to your customers.': 'المطبوعات لعملائك.',
  'Assigned Theme': 'السمة المعيّنة',
  'Live Preview': 'معاينة مباشرة',
  'View Full Screen Preview': 'عرض المعاينة بملء الشاشة',
  'Assign a theme from above to change the layout.': 'عيّن سمة من الأعلى لتغيير التخطيط.',
  'Landing Page Style': 'نمط الصفحة الرئيسية',
  'Adjust the visual vibe of your public catalog.': 'اضبط الطابع المرئي للكتالوج العام.',
  'Theme Engine': 'محرك السمات',
  'Hero Title Override': 'عنوان الواجهة البديل',
  'Hero Description Override': 'وصف الواجهة البديل',
  "Describe your brand's vision...": 'صِف رؤية علامتك التجارية...',
  'WhatsApp Number for Checkout': 'رقم واتساب لإتمام الطلب',
  'Include country code without + or spaces (e.g. 2010...)': 'أدخل رمز الدولة من دون + أو مسافات (مثال: 2010...)',
  'Hide Prices': 'إخفاء الأسعار',
  "Show 'Contact for Price' instead of values": 'عرض «تواصل لمعرفة السعر» بدلًا من القيم',
  'Pro Tip: Brutalism': 'نصيحة احترافية: النمط الوحشي',
  'The Brutalist theme thrives on bold typography and high contrast. Use short, punchy hero titles for the best impact.': 'تتميز السمة الوحشية بخطوط جريئة وتباين مرتفع. استخدم عناوين واجهة قصيرة وقوية لتحقيق أفضل تأثير.',
  'Live Preview (Mobile Visualization)': 'معاينة مباشرة (عرض الهاتف)',
  'Explore': 'استكشف',
  'Public Link': 'الرابط العام',
  'Open Catalog': 'فتح الكتالوج',
  'Staff Management': 'إدارة الموظفين',
  'Manage users who have access to your organization and their permissions.': 'إدارة المستخدمين الذين يمكنهم الوصول إلى المؤسسة وصلاحياتهم.',
  'No staff members found': 'لم يتم العثور على موظفين',
  'Add your first staff member to get started.': 'أضف أول موظف للبدء.',
  'Print Layout Preview': 'معاينة تخطيط الطباعة',
  'Company Logo': 'شعار الشركة',
  'Bill To': 'فاتورة إلى',
  'Invoice Number': 'رقم الفاتورة',
  'Invoice Date': 'تاريخ الفاتورة',
  'Qty': 'الكمية',
  'Unit Price': 'سعر الوحدة',
  'Subtotal': 'الإجمالي الفرعي',
  'Tax (14%)': 'الضريبة (14٪)',
  'Total EGP': 'الإجمالي بالجنيه المصري',
  'Payment Details': 'تفاصيل الدفع',
  'Acc:': 'الحساب:',
  'IBAN:': 'رقم IBAN:',
  'Notes': 'ملاحظات',
  'Print Preview': 'معاينة الطباعة',
  'Theme Name': 'اسم السمة',
  'Logo URL': 'رابط الشعار',
  'Upload Logo': 'رفع الشعار',
  'Upload an image and we will set the Logo URL automatically': 'ارفع صورة وسنضبط رابط الشعار تلقائيًا',
  'Primary Color': 'اللون الأساسي',
  'Font Family': 'عائلة الخط',
  'Invoice Footer Text': 'نص تذييل الفاتورة',
  'Thank you for your business...': 'شكرًا لتعاملكم معنا...',
  'Organization Details': 'بيانات المؤسسة',
  'Address, Phone, Website, etc.': 'العنوان والهاتف والموقع الإلكتروني وغيرها',
  'Invoice Print Settings': 'إعدادات طباعة الفاتورة',
  'Customize printed invoice layout per branding theme (logo override and bank details)': 'تخصيص تخطيط الفاتورة المطبوعة لكل سمة هوية بصرية (الشعار البديل والبيانات البنكية)',
  'Logo Override URL': 'رابط الشعار البديل',
  'Leave empty to use the branding theme logo': 'اتركه فارغًا لاستخدام شعار سمة الهوية البصرية',
  'Logo Size': 'حجم الشعار',
  'Logo Alignment': 'محاذاة الشعار',
  'Company Info Alignment': 'محاذاة بيانات الشركة',
  'Layout Preview': 'معاينة التخطيط',
  'Logo Preview': 'معاينة الشعار',
  'Use the alignment selectors above to "move" elements around.': 'استخدم محددات المحاذاة أعلاه لتغيير مواضع العناصر.',
  'Show Bank Details': 'إظهار البيانات البنكية',
  'Display bank details block on printed invoice': 'إظهار قسم البيانات البنكية في الفاتورة المطبوعة',
  'Bank Details': 'البيانات البنكية',
  'Account': 'الحساب',
  'Label (optional)': 'التسمية (اختيارية)',
  'Main Account': 'الحساب الرئيسي',
  'Account Name': 'اسم الحساب',
  'Bank Account Number': 'رقم الحساب البنكي',
  'Add Bank Account': 'إضافة حساب بنكي',
  'Payment Instructions (optional)': 'تعليمات الدفع (اختيارية)',
  'Please reference invoice number on transfer.': 'يرجى ذكر رقم الفاتورة عند التحويل.',
  'Watermark': 'العلامة المائية',
  'Enable Watermark': 'تفعيل العلامة المائية',
  'Show a watermark overlay on printed invoices': 'إظهار علامة مائية فوق الفواتير المطبوعة',
  'Watermark Type': 'نوع العلامة المائية',
  'Text Only': 'نص فقط',
  'Image': 'صورة',
  'Watermark Opacity': 'شفافية العلامة المائية',
  'Auto Status Text': 'نص الحالة التلقائي',
  'Automatically show DRAFT, PAID, or VOID based on invoice status': 'إظهار DRAFT أو PAID أو VOID تلقائيًا وفق حالة الفاتورة',
  'Configure access level and permissions': 'تكوين مستوى الوصول والصلاحيات',
  'Access Level': 'مستوى الوصول',
  'Standard User': 'مستخدم عادي',
  'Organization Admin': 'مسؤول المؤسسة',
  'Admins can manage other staff and organization settings.': 'يمكن للمسؤولين إدارة الموظفين الآخرين وإعدادات المؤسسة.',

  // SettingsView — messages and options
  'Logo file size must be less than 200KB': 'يجب ألا يتجاوز حجم ملف الشعار 200 كيلوبايت',
  'Logo file size must be less than 5MB': 'يجب ألا يتجاوز حجم ملف الشعار 5 ميجابايت',
  'Logo uploaded successfully': 'تم رفع الشعار بنجاح',
  'Failed to upload logo': 'تعذر رفع الشعار',
  'Failed to load branding themes': 'تعذر تحميل سمات الهوية البصرية',
  'Theme Name is required': 'اسم السمة مطلوب',
  'Branding theme updated successfully': 'تم تحديث سمة الهوية البصرية بنجاح',
  'Branding theme created successfully': 'تم إنشاء سمة الهوية البصرية بنجاح',
  'Failed to save branding theme': 'تعذر حفظ سمة الهوية البصرية',
  'Are you sure you want to delete this branding theme?': 'هل أنت متأكد من حذف سمة الهوية البصرية هذه؟',
  'Branding theme deleted': 'تم حذف سمة الهوية البصرية',
  'Failed to delete branding theme': 'تعذر حذف سمة الهوية البصرية',
  'Staff provisioning is not migrated to local PostgreSQL yet.': 'لم تُنقل تهيئة حسابات الموظفين إلى PostgreSQL المحلي بعد.',
  'Full Name and Email are required': 'الاسم الكامل والبريد الإلكتروني مطلوبان',
  'Staff updated successfully': 'تم تحديث بيانات الموظف بنجاح',
  'Staff added successfully': 'تمت إضافة الموظف بنجاح',
  'Failed to save staff': 'تعذر حفظ بيانات الموظف',
  'Are you sure you want to remove this staff member from your organization?': 'هل أنت متأكد من إزالة هذا الموظف من مؤسستك؟',
  'Staff removed from organization': 'تمت إزالة الموظف من المؤسسة',
  'Failed to remove staff': 'تعذرت إزالة الموظف',
  'Core Chart of Accounts initialized and default accounts linked': 'تمت تهيئة دليل الحسابات الأساسي وربط الحسابات الافتراضية',
  'Failed to seed accounts': 'تعذرت تهيئة الحسابات',
  'Failed to load settings': 'تعذر تحميل الإعدادات',
  'Catalog settings are not migrated to local PostgreSQL yet.': 'لم تُنقل إعدادات الكتالوج إلى PostgreSQL المحلي بعد.',
  'Settings saved successfully': 'تم حفظ الإعدادات بنجاح',
  'Failed to save settings': 'تعذر حفظ الإعدادات',
  'Currency already added': 'تمت إضافة العملة مسبقًا',
  'Quote number reset and saved': 'تمت إعادة ضبط رقم عرض السعر وحفظه',
  'Invoice number reset and saved': 'تمت إعادة ضبط رقم الفاتورة وحفظه',
  'Fixed Assets number reset and saved': 'تمت إعادة ضبط رقم الأصول الثابتة وحفظه',

  // Currency option names
  'US Dollar': 'الدولار الأمريكي',
  'Euro': 'اليورو',
  'British Pound': 'الجنيه الإسترليني',
  'Japanese Yen': 'الين الياباني',
  'Australian Dollar': 'الدولار الأسترالي',
  'Canadian Dollar': 'الدولار الكندي',
  'Swiss Franc': 'الفرنك السويسري',
  'Chinese Yuan': 'اليوان الصيني',
  'UAE Dirham': 'الدرهم الإماراتي',
  'Saudi Riyal': 'الريال السعودي',
  'Egyptian Pound': 'الجنيه المصري',
  'Indian Rupee': 'الروبية الهندية',
  'Brazilian Real': 'الريال البرازيلي',
  'Russian Ruble': 'الروبل الروسي',
  'South African Rand': 'الراند الجنوب أفريقي',
  'Mexican Peso': 'البيزو المكسيكي',
  'Singapore Dollar': 'الدولار السنغافوري',
  'Hong Kong Dollar': 'دولار هونغ كونغ',
  'New Zealand Dollar': 'الدولار النيوزيلندي',
  'Turkish Lira': 'الليرة التركية',
  'South Korean Won': 'الوون الكوري الجنوبي',
  'Indonesian Rupiah': 'الروبية الإندونيسية',
  'Thai Baht': 'البات التايلندي',
  'Malaysian Ringgit': 'الرينغيت الماليزي',
  'Philippine Peso': 'البيزو الفلبيني',
  'Vietnamese Dong': 'الدونغ الفيتنامي',
  'Pakistani Rupee': 'الروبية الباكستانية',
  'Nigerian Naira': 'النيرة النيجيرية',
  'Argentine Peso': 'البيزو الأرجنتيني',
  'Chilean Peso': 'البيزو التشيلي',
  'Colombian Peso': 'البيزو الكولومبي',
  'Peruvian Sol': 'السول البيروفي',
  'Qatari Rial': 'الريال القطري',
  'Kuwaiti Dinar': 'الدينار الكويتي',
  'Bahraini Dinar': 'الدينار البحريني',
  'Omani Rial': 'الريال العُماني',
  'Jordanian Dinar': 'الدينار الأردني',
  'Lebanese Pound': 'الليرة اللبنانية',
  'Israeli New Shekel': 'الشيكل الإسرائيلي الجديد',

  // ProfileView
  'User Profile': 'الملف الشخصي',
  'Manage your account information and preferences.': 'إدارة معلومات حسابك وتفضيلاتك.',
  'Personal Information': 'المعلومات الشخصية',
  'Email address cannot be changed as it is linked to your account.': 'لا يمكن تغيير البريد الإلكتروني لأنه مرتبط بحسابك.',
  'Language Preferences': 'تفضيلات اللغة',
  'Display Language': 'لغة العرض',
  'English': 'الإنجليزية',
  'Theme & Layout Preferences': 'تفضيلات المظهر والتخطيط',
  'Choose your preferred dashboard aesthetic and color scheme.': 'اختر المظهر ونظام الألوان المفضلين للوحة التحكم.',
  'Live Sync': 'مزامنة فورية',
  'Classic Slate': 'أردوازي كلاسيكي',
  'Enterprise': 'مؤسسي',
  'Slate sidebar & sky blue accents': 'شريط جانبي أردوازي ولمسات زرقاء سماوية',
  'Modern Indigo': 'نيلي عصري',
  'Recommended': 'موصى به',
  'Clean glass card layout': 'تخطيط بطاقات زجاجية أنيق',
  'Pastel Greenery': 'خضرة هادئة',
  'Organic': 'طبيعي',
  'Sage green & forest mint': 'أخضر مريمي ونعناعي غابي',
  'Cherry Blossom': 'زهر الكرز',
  'Vibrant': 'حيوي',
  'Sakura rose & fuchsia': 'وردي ساكورا وفوشيا',
  'Monochrome Minimal': 'أحادي بسيط',
  'Sleek': 'انسيابي',
  'High precision neutral theme': 'سمة محايدة عالية الدقة',
  'Technical Dark': 'تقني داكن',
  'Developer': 'للمطورين',
  'Dark workspace & neon green': 'مساحة عمل داكنة وأخضر نيون',
  'Neo Brutalist': 'وحشي حديث',
  'High Contrast': 'تباين مرتفع',
  'Bold black borders & pop yellow': 'حدود سوداء جريئة ولمسات صفراء',
  'Palette:': 'لوحة الألوان:',
  'Profile updated successfully': 'تم تحديث الملف الشخصي بنجاح',
  'Failed to update profile': 'تعذر تحديث الملف الشخصي',
  'Failed to load profile data': 'تعذر تحميل بيانات الملف الشخصي',

  // EmployeesView and EmployeeProfileView
  'Employees': 'الموظفون',
  'Manage your workforce and payroll details': 'إدارة القوى العاملة وبيانات الرواتب',
  'Import Employees': 'استيراد الموظفين',
  'Add Employee': 'إضافة موظف',
  'Search employees...': 'ابحث عن موظفين...',
  'Loading employees...': 'جاري تحميل الموظفين...',
  'No employees found': 'لم يتم العثور على موظفين',
  'Add your first employee to start managing payroll': 'أضف أول موظف لبدء إدارة الرواتب',
  'Joined': 'تاريخ الانضمام',
  'Enter employee details and payroll information': 'أدخل بيانات الموظف ومعلومات الراتب',
  'First Name *': 'الاسم الأول *',
  'Last Name *': 'اسم العائلة *',
  'Employment Details': 'تفاصيل التوظيف',
  'Job Title': 'المسمى الوظيفي',
  'Job Title *': 'المسمى الوظيفي *',
  'Joining Date': 'تاريخ الالتحاق',
  'On Leave': 'في إجازة',
  'Payroll & Banking': 'الرواتب والبيانات البنكية',
  'Base Salary *': 'الراتب الأساسي *',
  'Payment Frequency': 'دورية الدفع',
  'Bank Name': 'اسم البنك',
  'Account Number': 'رقم الحساب',
  'Additional Notes': 'ملاحظات إضافية',
  'Internal notes about this employee...': 'ملاحظات داخلية عن هذا الموظف...',
  'Delete Employee': 'حذف الموظف',
  'Employee updated successfully': 'تم تحديث بيانات الموظف بنجاح',
  'Employee added successfully': 'تمت إضافة الموظف بنجاح',
  'Failed to save employee': 'تعذر حفظ بيانات الموظف',
  'Employee deleted successfully': 'تم حذف الموظف بنجاح',
  'Failed to delete employee': 'تعذر حذف الموظف',
  'Edit Profile': 'تعديل الملف',
  'Overview': 'نظرة عامة',
  'Activity Logs': 'سجلات النشاط',
  'Assets': 'العُهد والأصول',
  'Documents': 'المستندات',
  'Identification': 'إثبات الهوية',
  'Profile Details': 'تفاصيل الملف',
  'Payroll Information': 'معلومات الراتب',
  'Internal Notes': 'ملاحظات داخلية',
  'Attendance History': 'سجل الحضور',
  'Last 7 Days': 'آخر 7 أيام',
  'Last 30 Days': 'آخر 30 يومًا',
  '3 Months': '3 أشهر',
  'Day': 'اليوم',
  'Check In': 'الحضور',
  'Check Out': 'الانصراف',
  'Duration': 'المدة',
  'System Activity': 'نشاط النظام',
  'Assigned Assets': 'العُهد المعيّنة',
  'Register Asset': 'تسجيل عهدة',
  'No assets assigned yet.': 'لا توجد عُهد معيّنة بعد.',
  '• S/N:': '• الرقم التسلسلي:',
  'Given:': 'تاريخ التسليم:',
  'Signature': 'التوقيع',
  'Document': 'مستند',
  'Employee Documents': 'مستندات الموظف',
  'Add Document': 'إضافة مستند',
  'No documents attached yet.': 'لا توجد مستندات مرفقة بعد.',
  'Uploaded:': 'تاريخ الرفع:',
  'View Document': 'عرض المستند',
  'Asset Name': 'اسم العهدة',
  'e.g. MacBook Pro M2': 'مثال: MacBook Pro M2',
  'Asset Type': 'نوع العهدة',
  'Laptop': 'حاسوب محمول',
  'Phone': 'هاتف',
  'Vehicle': 'مركبة',
  'Keys': 'مفاتيح',
  'Hardware/Equipment': 'أجهزة/معدات',
  'Serial Number': 'الرقم التسلسلي',
  'Given Date': 'تاريخ التسليم',
  'Upload Document / Proof': 'رفع مستند / إثبات',
  'Employee Signature': 'توقيع الموظف',
  'Save Asset & Signature': 'حفظ العهدة والتوقيع',
  'Document Title': 'عنوان المستند',
  'e.g. Passport Copy': 'مثال: صورة جواز السفر',
  'Identification (ID, Passport)': 'إثبات الهوية (بطاقة، جواز سفر)',
  'Education (Degree, Certificate)': 'التعليم (درجة علمية، شهادة)',
  'Military / Army': 'الخدمة العسكرية',
  'Contract / Agreement': 'عقد / اتفاقية',
  'Medical': 'طبي',
  'Upload Document / Link': 'رفع مستند / رابط',
  'Upload from device...': 'الرفع من الجهاز...',
  'or paste link': 'أو الصق رابطًا',
  'Save Document': 'حفظ المستند',
  'Asset registered': 'تم تسجيل العهدة',
  'Failed to add asset': 'تعذرت إضافة العهدة',
  'File uploaded': 'تم رفع الملف',
  'Upload failed': 'فشل الرفع',
  'Document attached': 'تم إرفاق المستند',
  'Failed to add document': 'تعذرت إضافة المستند',
  'Asset deleted': 'تم حذف العهدة',
  'Document deleted': 'تم حذف المستند',

  // EmployeeImport
  'Upload a CSV file containing your employee records. Use our template to ensure correct formatting.': 'ارفع ملف CSV يحتوي على سجلات الموظفين. استخدم القالب لضمان صحة التنسيق.',
  'Download CSV Template': 'تنزيل قالب CSV',
  'File to upload': 'الملف المطلوب رفعه',
  'Drag and drop CSV file or select manually': 'اسحب ملف CSV وأفلته هنا أو اختره يدويًا',
  'Select file': 'اختيار ملف',
  'Review employees': 'مراجعة الموظفين',
  'We found': 'عثرنا على',
  'employees in your file. Review them before importing.': 'موظفين في ملفك. راجع بياناتهم قبل الاستيراد.',
  'Salary': 'الراتب',
  'Ready to import': 'جاهز للاستيراد',
  'These employees will be added to your organization. You can manage their payroll details after import.': 'سيُضاف هؤلاء الموظفون إلى مؤسستك. يمكنك إدارة تفاصيل رواتبهم بعد الاستيراد.',
  'Importing employees': 'جاري استيراد الموظفين',
  'Import stopped after': 'توقف الاستيراد بعد',
  'employees:': 'موظفين:',
  'Complete Import': 'إكمال الاستيراد',

  // PayRunView and PayrollSettingsView
  'PayRuns': 'دورات الرواتب',
  'PayRun': 'دورة رواتب',
  'Process and manage employee payroll cycles': 'معالجة دورات رواتب الموظفين وإدارتها',
  'New PayRun': 'دورة رواتب جديدة',
  'Draft PayRuns': 'دورات الرواتب المسودة',
  'Paid This Month': 'المدفوع هذا الشهر',
  'Search payruns...': 'ابحث في دورات الرواتب...',
  'Loading payruns...': 'جاري تحميل دورات الرواتب...',
  'No payruns found': 'لم يتم العثور على دورات رواتب',
  'Create your first payrun to process payroll': 'أنشئ أول دورة رواتب لمعالجة الرواتب',
  'Total Net': 'إجمالي الصافي',
  'Mark as Paid': 'تحديد كمدفوع',
  'Set the period and payment date for this payroll cycle': 'حدّد الفترة وتاريخ الدفع لدورة الرواتب هذه',
  'Cycle Configuration': 'إعداد الدورة',
  'PayRun Name *': 'اسم دورة الرواتب *',
  'e.g. March 2024 Payroll': 'مثال: رواتب مارس 2024',
  'Period Start *': 'بداية الفترة *',
  'Period End *': 'نهاية الفترة *',
  'Payment Date *': 'تاريخ الدفع *',
  'Summary Overview': 'ملخص عام',
  'Active Employees': 'الموظفون النشطون',
  'Estimated Gross': 'الإجمالي التقديري',
  'Total Net Pay': 'إجمالي صافي الرواتب',
  'This payrun will include all active employees. You can adjust individual line items after creating the draft.': 'ستشمل دورة الرواتب جميع الموظفين النشطين. يمكنك تعديل البنود الفردية بعد إنشاء المسودة.',
  'Employee Breakdown': 'تفصيل الموظفين',
  'Gross:': 'الإجمالي:',
  'Net Pay': 'صافي الراتب',
  'Allowances': 'البدلات',
  'No allowances': 'لا توجد بدلات',
  'Deductions': 'الاستقطاعات',
  'No deductions': 'لا توجد استقطاعات',
  'No active employees found for this organization.': 'لا يوجد موظفون نشطون في هذه المؤسسة.',
  'Create Draft PayRun': 'إنشاء مسودة دورة رواتب',
  'Record Payment': 'تسجيل الدفع',
  'Paid From *': 'الدفع من *',
  'Optional reference number': 'رقم مرجعي اختياري',
  'Recording...': 'جاري التسجيل...',
  'Delete PayRun': 'حذف دورة الرواتب',
  'Bank Transfer': 'تحويل بنكي',
  'Payroll Expense': 'مصروف الرواتب',
  'Payroll Liability': 'التزام الرواتب',
  'Payroll': 'الرواتب',
  'Payrun updated successfully': 'تم تحديث دورة الرواتب بنجاح',
  'Payrun created successfully': 'تم إنشاء دورة الرواتب بنجاح',
  'Failed to save payrun': 'تعذر حفظ دورة الرواتب',
  'Payrun deleted successfully': 'تم حذف دورة الرواتب بنجاح',
  'Failed to delete payrun': 'تعذر حذف دورة الرواتب',
  'Failed to update status': 'تعذر تحديث الحالة',
  'Please select a payment account': 'يرجى اختيار حساب الدفع',
  'Payrun marked as paid and transaction recorded': 'تم تحديد دورة الرواتب كمدفوعة وتسجيل المعاملة',
  'Failed to record payment': 'تعذر تسجيل الدفع',
  'Payroll Settings': 'إعدادات الرواتب',
  'Configure your company payroll rules and defaults': 'تكوين قواعد الرواتب والإعدادات الافتراضية للشركة',
  'Company Details': 'بيانات الشركة',
  'Legal Company Name': 'الاسم القانوني للشركة',
  'e.g. Acme Corp': 'مثال: Acme Corp',
  'Tax Identification Number': 'الرقم التعريفي الضريبي',
  'Salary / Wages Account': 'حساب الرواتب والأجور',
  'Select Expense Account...': 'اختر حساب مصروفات...',
  'Payroll Payable / Liability Account': 'حساب الرواتب المستحقة / الالتزامات',
  'Select Liability Account...': 'اختر حساب التزام...',
  'Payment Cycle': 'دورة الدفع',
  'Pay Frequency': 'دورية الراتب',
  'Default Pay Day (of month)': 'يوم الدفع الافتراضي (من الشهر)',
  'e.g. USD, EGP, EUR': 'مثال: USD، EGP، EUR',
  'Standard Deductions': 'الاستقطاعات القياسية',
  'Add Deduction': 'إضافة استقطاع',
  'e.g. Income Tax': 'مثال: ضريبة الدخل',
  'Rate (%)': 'النسبة (٪)',
  'Standard Allowances': 'البدلات القياسية',
  'Add Allowance': 'إضافة بدل',
  'e.g. Housing': 'مثال: بدل السكن',
  'Payroll settings saved successfully': 'تم حفظ إعدادات الرواتب بنجاح',

  // AttendanceView
  'Attendance Control': 'إدارة الحضور والانصراف',
  'Loading attendance system...': 'جاري تحميل نظام الحضور...',
  'Check Out Now': 'تسجيل الانصراف الآن',
  'Check In Now': 'تسجيل الحضور الآن',
  'Current Session Info': 'معلومات الجلسة الحالية',
  'Device ID': 'معرّف الجهاز',
  'Network IP': 'عنوان IP للشبكة',
  'Location Status': 'حالة الموقع',
  'GPS Ready': 'نظام GPS جاهز',
  'Last 50 Records': 'آخر 50 سجلًا',
  'Metadata': 'البيانات الوصفية',
  'No attendance records found yet.': 'لا توجد سجلات حضور حتى الآن.',
  'In progress...': 'قيد التنفيذ...',
  'Check-in Location': 'موقع تسجيل الحضور',
  'Lat:': 'خط العرض:',
  'Lng:': 'خط الطول:',
  'Network Details': 'تفاصيل الشبكة',
  'IN:': 'الحضور:',
  'OUT:': 'الانصراف:',
  'Checked In': 'تم تسجيل الحضور',
  'Successfully checked in!': 'تم تسجيل الحضور بنجاح!',
  'Failed to check in. Please try again.': 'تعذر تسجيل الحضور. يرجى المحاولة مرة أخرى.',
  'Successfully checked out!': 'تم تسجيل الانصراف بنجاح!',
  'Failed to check out. Please try again.': 'تعذر تسجيل الانصراف. يرجى المحاولة مرة أخرى.',
  'Please check in to record your working hours.': 'يرجى تسجيل الحضور لاحتساب ساعات العمل.',

  // TasksMOPSView
  'Task/Activities': 'المهام والأنشطة',
  'Create, assign and track operational tasks and activities': 'إنشاء المهام والأنشطة التشغيلية وإسنادها ومتابعتها',
  'New Task': 'مهمة جديدة',
  'Search tasks or assignees...': 'ابحث عن مهام أو مكلّفين...',
  'Loading tasks...': 'جاري تحميل المهام...',
  'No tasks found': 'لم يتم العثور على مهام',
  'Create your first task to start tracking operations': 'أنشئ أول مهمة لبدء متابعة العمليات',
  'Priority': 'الأولوية',
  'Open Thread': 'فتح المناقشة',
  'Request Extension': 'طلب تمديد',
  'Assigned to:': 'مُسندة إلى:',
  'Unassigned': 'غير مُسندة',
  'Step': 'الخطوة',
  'of': 'من',
  'Current Step:': 'الخطوة الحالية:',
  'Complete Step': 'إكمال الخطوة',
  'Define and assign operational goals': 'تحديد الأهداف التشغيلية وإسنادها',
  'Task Title': 'عنوان المهمة',
  'e.g. Monthly Inventory Audit': 'مثال: مراجعة المخزون الشهرية',
  'Describe the task details...': 'صِف تفاصيل المهمة...',
  'This task has a multi-step workflow': 'تتضمن هذه المهمة سير عمل متعدد الخطوات',
  'Assign To (Select Multiple)': 'الإسناد إلى (يمكن اختيار عدة أشخاص)',
  'No employees found in this organization': 'لم يتم العثور على موظفين في هذه المؤسسة',
  'Workflow Steps': 'خطوات سير العمل',
  'Add Step': 'إضافة خطوة',
  "No steps added. Click 'Add Step' to start building the workflow.": 'لم تُضف خطوات. انقر على «إضافة خطوة» لبدء إنشاء سير العمل.',
  'e.g. Initial Review': 'مثال: المراجعة الأولية',
  'Stage Description': 'وصف المرحلة',
  'Describe the objectives of this stage...': 'صِف أهداف هذه المرحلة...',
  'Stage Requirements': 'متطلبات المرحلة',
  'List exact requirements before moving forward...': 'اذكر المتطلبات الدقيقة قبل الانتقال للمرحلة التالية...',
  'Assign To': 'إسناد إلى',
  'In Progress': 'قيد التنفيذ',
  'Request More Time': 'طلب وقت إضافي',
  'Edit Task': 'تعديل المهمة',
  'Delete Task': 'حذف المهمة',
  'Task Overview': 'نظرة عامة على المهمة',
  'Workflow Progress': 'تقدم سير العمل',
  'Working On This Now': 'يعمل على هذه الخطوة الآن',
  'Finalize Stage & Move to Next': 'إنهاء المرحلة والانتقال إلى التالية',
  'Discussions': 'المناقشات',
  'Back to Threads': 'العودة إلى المناقشات',
  'Start a New Thread': 'بدء مناقشة جديدة',
  'Thread title or topic...': 'عنوان المناقشة أو موضوعها...',
  'Existing Discussions': 'المناقشات الحالية',
  'replies': 'ردود',
  'No discussion threads yet.': 'لا توجد مناقشات بعد.',
  'Thread started by': 'بدأ المناقشة',
  'No replies yet. Be the first to reply!': 'لا توجد ردود بعد. كن أول من يرد!',
  'Type your reply...': 'اكتب ردك...',
  'Current Due Date:': 'تاريخ الاستحقاق الحالي:',
  'How many extra days?': 'كم عدد الأيام الإضافية؟',
  'Reason for extension': 'سبب التمديد',
  'Why do you need more time?': 'لماذا تحتاج إلى وقت إضافي؟',
  'Submit Request': 'إرسال الطلب',
  'My Requests': 'طلباتي',
  'days': 'أيام',
  'Remove My Pending Request': 'إزالة طلبي المعلّق',
  "You haven't requested any extensions yet.": 'لم تطلب أي تمديد بعد.',
  'Approvals Needed': 'طلبات تحتاج إلى اعتماد',
  'No pending requests': 'لا توجد طلبات معلّقة',
  'Please add at least one step for the workflow': 'يرجى إضافة خطوة واحدة على الأقل إلى سير العمل',
  'Task updated successfully': 'تم تحديث المهمة بنجاح',
  'New Task Assigned': 'تم إسناد مهمة جديدة',
  'Task created and assigned successfully': 'تم إنشاء المهمة وإسنادها بنجاح',
  'Failed to save task': 'تعذر حفظ المهمة',
  'Task deleted successfully': 'تم حذف المهمة بنجاح',
  'Failed to delete task': 'تعذر حذف المهمة',
  'New Discussion Thread on Task': 'مناقشة جديدة حول المهمة',
  'Thread started': 'تم بدء المناقشة',
  'Failed to start thread': 'تعذر بدء المناقشة',
  'New Reply in Task Discussion': 'رد جديد في مناقشة المهمة',
  'Reply sent': 'تم إرسال الرد',
  'Failed to send reply': 'تعذر إرسال الرد',
  'Extension Requested': 'تم طلب تمديد',
  'Extension request submitted': 'تم إرسال طلب التمديد',
  'Failed to submit request': 'تعذر إرسال الطلب',
  'Extension Approved': 'تم اعتماد التمديد',
  'Extension approved and due date updated': 'تم اعتماد التمديد وتحديث تاريخ الاستحقاق',
  'Failed to approve extension': 'تعذر اعتماد التمديد',
  'Extension request removed': 'تمت إزالة طلب التمديد',
  'Extension request rejected': 'تم رفض طلب التمديد',
  'Workflow Step Assigned': 'تم إسناد خطوة من سير العمل',

  // ActivityLogView and HistoryNotesView
  'Audit Trail': 'مسار التدقيق',
  'Immutable Security Logs & Action Tracking': 'سجلات أمان غير قابلة للتغيير وتتبع الإجراءات',
  'Live Monitoring Active': 'المراقبة المباشرة نشطة',
  'Search user, action, target...': 'ابحث بالمستخدم أو الإجراء أو الهدف...',
  'Decrypting Secure Logs...': 'جاري فك تشفير السجلات الآمنة...',
  'No forensic data found matching your query.': 'لم يتم العثور على بيانات تدقيق تطابق بحثك.',
  'View Forensic Detail': 'عرض تفاصيل التدقيق',
  'Delta Logged': 'تم تسجيل الفروقات',
  'Contextual Data': 'البيانات السياقية',
  'Page:': 'الصفحة:',
  'UA:': 'وكيل المستخدم:',
  'Data Transition (JSON Delta)': 'تغيّر البيانات (فروقات JSON)',
  '[-] DELETED/ORIGINAL': '[-] محذوف/أصلي',
  '[+] ADDED/MODIFIED': '[+] مضاف/معدّل',
  'Export as Signed JSON': 'تصدير كملف JSON موقّع',
  'Total Logs:': 'إجمالي السجلات:',
  'Filtered View': 'العرض المصفّى',
  'GDPR & ISO-27001 Compliant Audit Engine': 'محرك تدقيق متوافق مع GDPR وISO-27001',
  'History & Notes': 'السجل والملاحظات',
  'Add a note or comment... use @ to mention': 'أضف ملاحظة أو تعليقًا... استخدم @ للإشارة إلى شخص',
  'Add Note': 'إضافة ملاحظة',
  'No history or notes yet': 'لا يوجد سجل أو ملاحظات بعد',
  'Contact changed': 'تم تغيير جهة الاتصال',
  'View old and new values': 'عرض القيم القديمة والجديدة',
  'Old': 'قديم',
  'New': 'جديد',
  'New Mention': 'إشارة جديدة',
  'Note': 'ملاحظة',
  'Note added': 'تمت إضافة الملاحظة',
  'Failed to add note': 'تعذرت إضافة الملاحظة',
  'Note deleted': 'تم حذف الملاحظة',
  'Failed to delete note': 'تعذر حذف الملاحظة',

  // DiscussionsView
  'Loading discussions...': 'جاري تحميل المناقشات...',
  'Untitled Discussion': 'مناقشة بلا عنوان',
  'Untitled': 'بلا عنوان',
  'Anonymous': 'مجهول',
  'Sent photo': 'صورة مُرسلة',
  'Live Location': 'الموقع المباشر',
  'View on Google Maps': 'عرض على خرائط Google',
  'PDF / Document': 'ملف PDF / مستند',
  'vCard Contact': 'جهة اتصال vCard',
  'Reply': 'رد',
  'React': 'إضافة تفاعل',
  'Seen by:': 'شاهده:',
  'Microphone access denied': 'رُفض الوصول إلى الميكروفون',
  'Camera access denied': 'رُفض الوصول إلى الكاميرا',
  'My Location': 'موقعي',
  'Could not get your location': 'تعذر تحديد موقعك',
  'Geolocation is not supported by your browser': 'متصفحك لا يدعم تحديد الموقع الجغرافي',
  'Failed to load discussions': 'تعذر تحميل المناقشات',
  'Message reactions are not yet available in local mode.': 'تفاعلات الرسائل غير متاحة بعد في الوضع المحلي.',
  'Failed to add reaction': 'تعذرت إضافة التفاعل',
  'Failed to create discussion': 'تعذر إنشاء المناقشة',
  'Missing document context for this thread': 'سياق المستند مفقود لهذه المناقشة',
  'Target document no longer exists': 'المستند المستهدف لم يعد موجودًا',
  'Permission denied. You might not have access to this document.': 'تم رفض الإذن. قد لا تكون مخولًا للوصول إلى هذا المستند.',
  'Failed to post reply': 'تعذر نشر الرد',
  'Failed to create thread': 'تعذر إنشاء المناقشة',
  'By': 'بواسطة',
  'No replies yet. Be the first to join the conversation!': 'لا توجد ردود بعد. كن أول من يشارك في المحادثة!',
  'Retake': 'إعادة الالتقاط',
  'Send Photo': 'إرسال الصورة',
  'Camera': 'الكاميرا',
  'Gallery': 'المعرض',
  'Location': 'الموقع',
  'Type a message...': 'اكتب رسالة...',
  'Document Discussions': 'مناقشات المستند',
  'All Discussions': 'جميع المناقشات',
  'Threads Available': 'مناقشات متاحة',
  'New Thread': 'مناقشة جديدة',
  'Thread title...': 'عنوان المناقشة...',
  'No discussions found yet.': 'لا توجد مناقشات حتى الآن.',
  'Start a conversation': 'ابدأ محادثة',

  // HelpView
  'Help & Training': 'المساعدة والتدريب',
  'Admin Mode': 'وضع المسؤول',
  'Browse our documentation, video tutorials, and training materials developed to help you and your clients master the platform.': 'تصفح الوثائق وشروحات الفيديو والمواد التدريبية المصممة لمساعدتك وعملائك على إتقان المنصة.',
  'Search keywords...': 'ابحث بالكلمات المفتاحية...',
  'Help Home': 'الرئيسية للمساعدة',
  'No Help Content Yet': 'لا يوجد محتوى مساعدة بعد',
  'Click below to generate some starting categories and articles, or create your own from scratch.': 'انقر أدناه لإنشاء فئات ومقالات أولية، أو أنشئ محتواك من البداية.',
  'Populating...': 'جاري إنشاء المحتوى...',
  'Seed Default Content': 'إنشاء المحتوى الافتراضي',
  'Add Category': 'إضافة فئة',
  'Training Videos': 'فيديوهات التدريب',
  'Essential walk-throughs for new users.': 'جولات إرشادية أساسية للمستخدمين الجدد.',
  'Add Video': 'إضافة فيديو',
  'Add Resource': 'إضافة مورد',
  'No resources have been added to this category yet.': 'لم تُضف موارد إلى هذه الفئة بعد.',
  'Back to': 'العودة إلى',
  'Published': 'منشور',
  'This video resource is currently unavailable.': 'مورد الفيديو هذا غير متاح حاليًا.',
  'Training Resources': 'موارد التدريب',
  'Download additional materials for this session': 'تنزيل مواد إضافية لهذه الجلسة',
  'Other in': 'موارد أخرى في',
  'No other resources in this category.': 'لا توجد موارد أخرى في هذه الفئة.',
  'Need more help?': 'هل تحتاج إلى مزيد من المساعدة؟',
  "If this article didn't clear your doubts, feel free to reach out to our dedicated support team.": 'إذا لم تجب هذه المقالة عن استفساراتك، فتواصل مع فريق الدعم المتخصص.',
  'Contact Support': 'التواصل مع الدعم',
  'Category Title': 'عنوان الفئة',
  'Icon': 'الأيقونة',
  'Display Order': 'ترتيب العرض',
  'Resource Title': 'عنوان المورد',
  'Resource Type': 'نوع المورد',
  'Article': 'مقالة',
  'Video': 'فيديو',
  'Training Session': 'جلسة تدريبية',
  'Select a category...': 'اختر فئة...',
  'Content (Markdown supported)': 'المحتوى (يدعم Markdown)',
  'Video URL (YouTube/Vimeo)': 'رابط الفيديو (YouTube/Vimeo)',
  'Duration (e.g. 5:24)': 'المدة (مثال: 5:24)',
  'Resource Attachment URL': 'رابط مرفق المورد',
  'Getting Started': 'البدء',
  'Everything you need to know to get started with the platform.': 'كل ما تحتاج إلى معرفته لبدء استخدام المنصة.',
  'Invoices & Billing': 'الفواتير والتحصيل',
  'Managing your invoices, payments, and billing details.': 'إدارة الفواتير والمدفوعات وبيانات التحصيل.',
  'Account Settings': 'إعدادات الحساب',
  'Managing user profiles, roles, and organization settings.': 'إدارة ملفات المستخدمين وأدوارهم وإعدادات المؤسسة.',
  'Welcome to the Platform': 'مرحبًا بك في المنصة',
  'Platform Walkthrough': 'جولة في المنصة',
  'Default help content populated successfully': 'تم إنشاء محتوى المساعدة الافتراضي بنجاح',
  'Failed to populate default content': 'تعذر إنشاء المحتوى الافتراضي',
  'Failed to save category': 'تعذر حفظ الفئة',
  'Failed to save article': 'تعذر حفظ المقالة',
  'Are you sure you want to delete this category? All articles will remain orphan.': 'هل أنت متأكد من حذف هذه الفئة؟ ستبقى جميع المقالات من دون فئة.',
  'Category deleted': 'تم حذف الفئة',
  'Are you sure you want to delete this article?': 'هل أنت متأكد من حذف هذه المقالة؟',
  'Article deleted': 'تم حذف المقالة',

  // StoresView
  'Physical Stores': 'المتاجر الفعلية',
  'Manage your physical store locations and branches.': 'إدارة مواقع المتاجر والفروع الفعلية.',
  'Add Store': 'إضافة متجر',
  'Search stores...': 'ابحث عن متاجر...',
  'Store Name': 'اسم المتجر',
  'Address': 'العنوان',
  'Loading stores...': 'جاري تحميل المتاجر...',
  'No stores found. Click "Add Store" to create one.': 'لم يتم العثور على متاجر. انقر على «إضافة متجر» لإنشاء متجر.',
  'Archive Store': 'أرشفة المتجر',
  'Store Logo': 'شعار المتجر',
  'Upload Store Logo': 'رفع شعار المتجر',
  'Choose File': 'اختيار ملف',
  'e.g. Downtown Branch': 'مثال: فرع وسط المدينة',
  'Store physical address': 'العنوان الفعلي للمتجر',
  'Social Media Link': 'رابط التواصل الاجتماعي',
  'Terms and Conditions': 'الشروط والأحكام',
  'Terms to show at the bottom of the receipt': 'الشروط التي تظهر أسفل الإيصال',
  'POS Prefix': 'بادئة نقطة البيع',
  'Next Number': 'الرقم التالي',
  'Enforce Customer On Checkout': 'إلزام اختيار عميل عند الدفع',
  'Advanced Settings': 'الإعدادات المتقدمة',
  'Payment Channels': 'قنوات الدفع',
  'Select which bank accounts and payment-enabled accounts are available for this store.': 'اختر الحسابات البنكية والحسابات المفعّلة للدفع والمتاحة لهذا المتجر.',
  'Bank Accounts': 'الحسابات البنكية',
  'Other Payment Accounts': 'حسابات دفع أخرى',
  'No payment accounts found.': 'لم يتم العثور على حسابات دفع.',
  'Discount Settings': 'إعدادات الخصم',
  'Discount Notification Management': 'إدارة تنبيهات الخصم',
  'Add Rule': 'إضافة قاعدة',
  'Set discount limits for specific users. Users will be restricted to these values.': 'حدّد حدود الخصم لمستخدمين معينين، وسيُقيّدون بهذه القيم.',
  'Min Discount (%)': 'الحد الأدنى للخصم (٪)',
  'Max Discount (%)': 'الحد الأقصى للخصم (٪)',
  'Restricted Users': 'المستخدمون المقيّدون',
  'Select user to add...': 'اختر مستخدمًا لإضافته...',
  'No discount rules defined.': 'لم تُحدد قواعد خصم.',
  'Store name is required': 'اسم المتجر مطلوب',
  'Store updated successfully': 'تم تحديث المتجر بنجاح',
  'Store added successfully': 'تمت إضافة المتجر بنجاح',
  'Are you sure you want to archive this store? It cannot be deleted because it may contain sales data.': 'هل أنت متأكد من أرشفة هذا المتجر؟ لا يمكن حذفه لأنه قد يحتوي على بيانات مبيعات.',
  'Archiving store...': 'جاري أرشفة المتجر...',
  'Store archived successfully': 'تمت أرشفة المتجر بنجاح',
  'Failed to archive store': 'تعذرت أرشفة المتجر',
  'Logo file size must be less than 1MB': 'يجب ألا يتجاوز حجم ملف الشعار 1 ميجابايت',

  // UserAccessRightsView
  'Please select a user first': 'يرجى اختيار مستخدم أولًا',
  'Failed to update permissions': 'تعذر تحديث الصلاحيات',
  'No User Selected': 'لم يتم اختيار مستخدم',
  'Select a user from the Users tab to manage their': 'اختر مستخدمًا من تبويب المستخدمين لإدارة صلاحيات',
  'rights.': 'الوصول الخاصة به.',
  'Go to Users': 'الانتقال إلى المستخدمين',
  'View': 'عرض',
  'Manage': 'إدارة',
  'access for': 'الوصول إلى',
  'module': 'الوحدة',
  'Loading Security Protocols...': 'جاري تحميل بروتوكولات الأمان...',
  'Users': 'المستخدمون',
  'Edit Rights': 'صلاحيات التعديل',
  'View Rights': 'صلاحيات العرض',
  'Search staff members...': 'ابحث عن موظفين...',
  'Total Staff:': 'إجمالي الموظفين:',
  'Manage Permissions': 'إدارة الصلاحيات',
  'Try adjusting your search terms or add a new staff member.': 'جرّب تعديل كلمات البحث أو إضافة موظف جديد.',
  'Unknown Staff': 'موظف غير معروف',

  // LicenseGate and application auth/license wording
  'Authenticating Session...': 'جاري التحقق من الجلسة...',
  'Activation Required': 'التفعيل مطلوب',
  'Please enter your license key to unlock the application.': 'يرجى إدخال مفتاح الترخيص لفتح التطبيق.',
  'License Key': 'مفتاح الترخيص',
  'Activate License': 'تفعيل الترخيص',
  'Unique Device ID:': 'معرّف الجهاز الفريد:',
  'Your personal data remains stored locally on this device.': 'تظل بياناتك الشخصية مخزنة محليًا على هذا الجهاز.',
  'Licensed Session': 'جلسة مرخّصة',
  'Deactivate this device': 'إلغاء تفعيل هذا الجهاز',
  'Invalid license key': 'مفتاح الترخيص غير صالح',
  'License has expired': 'انتهت صلاحية الترخيص',
  'Connection to license server failed. Please check your internet.': 'تعذر الاتصال بخادم الترخيص. يرجى التحقق من اتصال الإنترنت.',
  'License validated and activated': 'تم التحقق من الترخيص وتفعيله',
  'License expired': 'انتهت صلاحية الترخيص',
  'Device limit reached': 'تم بلوغ حد الأجهزة',
  'Sign in with Google for a passwordless experience, or enter your email for direct access.': 'سجّل الدخول باستخدام Google من دون كلمة مرور، أو أدخل بريدك الإلكتروني للوصول المباشر.',
  'Sign in with Google (Passwordless)': 'تسجيل الدخول باستخدام Google (من دون كلمة مرور)',
  'Or continue with email': 'أو المتابعة باستخدام البريد الإلكتروني',
  'Username or Email': 'اسم المستخدم أو البريد الإلكتروني',
  'Enter username or email': 'أدخل اسم المستخدم أو البريد الإلكتروني',
  'Password': 'كلمة المرور',
  'Enter password': 'أدخل كلمة المرور',
  'Sign In': 'تسجيل الدخول',
  'Or Use License Key': 'أو استخدم مفتاح الترخيص',
  'Please enter your username or email address': 'يرجى إدخال اسم المستخدم أو البريد الإلكتروني',
  'Please enter your password': 'يرجى إدخال كلمة المرور',
  'Logged in successfully': 'تم تسجيل الدخول بنجاح',
  'Failed to log in': 'تعذر تسجيل الدخول',
  'Failed to login': 'تعذر تسجيل الدخول',
  'Login failed': 'فشل تسجيل الدخول',
  'Enter your email above to sign in.': 'أدخل بريدك الإلكتروني أعلاه لتسجيل الدخول.',
  'License activation failed': 'فشل تفعيل الترخيص',
  'License activated successfully!': 'تم تفعيل الترخيص بنجاح!',
  'Connection to license server failed': 'تعذر الاتصال بخادم الترخيص',
  'Your session has expired. Please sign in again.': 'انتهت صلاحية جلستك. يرجى تسجيل الدخول مرة أخرى.',
  'Session is no longer valid': 'لم تعد الجلسة صالحة',
  'Access Restricted': 'الوصول مقيّد',
  "Your account is either suspended or you haven't been assigned to any organization yet. Please contact your administrator for access.": 'حسابك موقوف أو لم يتم إسنادك إلى أي مؤسسة بعد. يرجى التواصل مع المسؤول للحصول على صلاحية الوصول.',
  'Working Offline - Changes will sync when connection is restored': 'أنت تعمل دون اتصال - ستتم مزامنة التغييرات عند استعادة الاتصال',
  'Local Development Organization': 'مؤسسة التطوير المحلية',

  // SuperAdminView — navigation and AI settings
  'Super Admin': 'المشرف العام',
  'Super Admin Panel': 'لوحة المشرف العام',
  'Manage client organizations, users, and product offerings.': 'إدارة مؤسسات العملاء والمستخدمين والمنتجات والخدمات.',
  'Organizations': 'المؤسسات',
  'Product & Service': 'المنتجات والخدمات',
  'License Keys': 'مفاتيح التراخيص',
  'AI Settings': 'إعدادات الذكاء الاصطناعي',
  'OpenAI-compatible AI provider': 'موفر ذكاء اصطناعي متوافق مع OpenAI',
  'Configure the server-side provider used by the organization-scoped floating assistant.': 'تكوين موفر الخادم الذي يستخدمه المساعد العائم المقيّد بالمؤسسة.',
  'AI provider settings are available when the PostgreSQL server session is active. Firebase-only legacy sessions cannot save these settings.': 'تتوفر إعدادات موفر الذكاء الاصطناعي عند تنشيط جلسة خادم PostgreSQL. لا يمكن للجلسات القديمة المعتمدة على Firebase فقط حفظ هذه الإعدادات.',
  'Loading provider settings…': 'جاري تحميل إعدادات الموفر…',
  'Provider name': 'اسم الموفر',
  'Model name': 'اسم النموذج',
  'OpenAI-compatible base URL': 'الرابط الأساسي المتوافق مع OpenAI',
  'Use a free/self-hosted endpoint such as Ollama, LiteLLM, or another service exposing': 'استخدم نقطة نهاية مجانية أو مستضافة ذاتيًا مثل Ollama أو LiteLLM أو خدمة أخرى توفر',
  '/chat/completions': '/chat/completions',
  'API key': 'مفتاح API',
  '(optional for local providers)': '(اختياري للموفرين المحليين)',
  'The key is encrypted and stored only on the server. It is never sent back to the browser.': 'يُشفّر المفتاح ويُخزن على الخادم فقط، ولا يُعاد إرساله إلى المتصفح.',
  'Enable organization AI': 'تفعيل ذكاء المؤسسة الاصطناعي',
  'Users can ask questions only about the organization selected in their switcher.': 'يمكن للمستخدمين طرح أسئلة عن المؤسسة المحددة في مبدّل المؤسسات فقط.',
  'Save AI settings': 'حفظ إعدادات الذكاء الاصطناعي',
  'Provider URL and model name are required': 'رابط الموفر واسم النموذج مطلوبان',
  'AI provider settings saved': 'تم حفظ إعدادات موفر الذكاء الاصطناعي',

  // SuperAdminView — licenses
  'License Key Management': 'إدارة مفاتيح التراخيص',
  'System Installers': 'مثبّتات النظام',
  'Windows': 'Windows',
  'Android': 'Android',
  'iOS': 'iOS',
  'Generate License': 'إنشاء ترخيص',
  'Expires:': 'ينتهي في:',
  'Devices:': 'الأجهزة:',
  'Download Android Installer (APK)': 'تنزيل مثبّت Android (APK)',
  'Download Windows Installer (EXE)': 'تنزيل مثبّت Windows (EXE)',
  'Download iOS Installer (IPA)': 'تنزيل مثبّت iOS (IPA)',
  'Log': 'السجل',
  'Reset Devices': 'إعادة ضبط الأجهزة',
  'Offline Version Support': 'دعم الإصدار غير المتصل',
  'Allow this license to be used in the offline APK with encrypted data.': 'السماح باستخدام هذا الترخيص في ملف APK غير المتصل مع بيانات مشفّرة.',
  'Assign to Organization': 'إسناد إلى مؤسسة',
  'No Organization Assigned': 'لا توجد مؤسسة معيّنة',
  'Duration (Days)': 'المدة (بالأيام)',
  'Device Limit': 'حد الأجهزة',
  'Permissions (Modules)': 'الصلاحيات (الوحدات)',
  'License updated!': 'تم تحديث الترخيص!',
  'License key generated!': 'تم إنشاء مفتاح الترخيص!',
  'Failed to toggle license status': 'تعذر تغيير حالة الترخيص',
  'License reset successfully': 'تمت إعادة ضبط الترخيص بنجاح',
  'Reset Devices?': 'إعادة ضبط الأجهزة؟',
  'This will disconnect all currently active devices for this license. Users will need to log in again.': 'سيؤدي ذلك إلى فصل جميع الأجهزة النشطة حاليًا لهذا الترخيص، وسيحتاج المستخدمون إلى تسجيل الدخول مرة أخرى.',
  'Yes, Reset': 'نعم، إعادة الضبط',
  'Activity Log': 'سجل النشاط',
  'No activity logs found for this key.': 'لم يتم العثور على سجلات نشاط لهذا المفتاح.',
  'Select Organization to Assign...': 'اختر مؤسسة للإسناد...',

  // SuperAdminView — organizations and subscriptions
  'Organizations Management': 'إدارة المؤسسات',
  'Add Organization': 'إضافة مؤسسة',
  'Configure organization details and module access': 'تكوين بيانات المؤسسة وصلاحيات الوصول إلى الوحدات',
  'Organization Name': 'اسم المؤسسة',
  'Target Audience': 'الجمهور المستهدف',
  'Click to pick location': 'انقر لاختيار الموقع',
  'Subscription Product': 'منتج الاشتراك',
  'Select a Product/Service': 'اختر منتجًا/خدمة',
  'Subscription Package': 'باقة الاشتراك',
  'Select a Package': 'اختر باقة',
  'Purchase Date': 'تاريخ الشراء',
  'Warning Date': 'تاريخ التنبيه',
  'Renewal Date': 'تاريخ التجديد',
  'Linked License Key': 'مفتاح الترخيص المرتبط',
  'No License Linked': 'لا يوجد ترخيص مرتبط',
  'Assigned Modules & Sub-modules': 'الوحدات والوحدات الفرعية المعيّنة',
  'Pick Location': 'اختيار الموقع',
  'Google Maps API Key Missing': 'مفتاح Google Maps API غير موجود',
  'Please configure': 'يرجى تكوين',
  'in your environment settings to enable the map picker.': 'في إعدادات البيئة لتفعيل أداة اختيار الموقع.',
  'Simulated Coordinates': 'إحداثيات محاكاة',
  'Latitude': 'خط العرض',
  'Longitude': 'خط الطول',
  'Click on the map to place the pin': 'انقر على الخريطة لوضع العلامة',
  'Edit Billing Details': 'تعديل بيانات الفوترة',
  'Edit Details': 'تعديل البيانات',
  'Total Users': 'إجمالي المستخدمين',
  'License Details': 'تفاصيل الترخيص',
  'Assigned Modules': 'الوحدات المعيّنة',
  'Assigned Users': 'المستخدمون المعيّنون',
  'User': 'المستخدم',
  'Key Person': 'الشخص الرئيسي',
  'No users assigned to this organization yet.': 'لم يُعيّن مستخدمون لهذه المؤسسة بعد.',
  'Subscription Details': 'تفاصيل الاشتراك',
  'Product / Service': 'المنتج / الخدمة',
  'Active Package': 'الباقة النشطة',
  'Please assign a Key Person first to send emails.': 'يرجى تعيين شخص رئيسي أولًا لإرسال رسائل البريد الإلكتروني.',
  'Send Warning Email': 'إرسال بريد تنبيه',
  'Send Renewal Email': 'إرسال بريد التجديد',
  'Email Composer': 'محرر البريد الإلكتروني',
  'Send subscription notification': 'إرسال إشعار الاشتراك',
  'Recipient (To)': 'المستلم (إلى)',
  'Additional Emails (CC)': 'رسائل إضافية (CC)',
  'Subject': 'الموضوع',
  'Email Subject': 'موضوع البريد الإلكتروني',
  'Message Body': 'نص الرسالة',
  'Write your message here...': 'اكتب رسالتك هنا...',
  'Opening Gmail...': 'جاري فتح Gmail...',
  'Send via Gmail': 'الإرسال عبر Gmail',
  'Edit Organization Details': 'تعديل بيانات المؤسسة',
  'Update Organization': 'تحديث المؤسسة',
  'Assign Modules': 'إسناد الوحدات',
  '(Main)': '(رئيسية)',
  'Select Product': 'اختر المنتج',
  'Select Package': 'اختر الباقة',
  'Please select a product first to see available packages.': 'يرجى اختيار منتج أولًا لعرض الباقات المتاحة.',
  'Update Billing Details': 'تحديث بيانات الفوترة',
  'Organization added successfully': 'تمت إضافة المؤسسة بنجاح',
  'Failed to add organization': 'تعذرت إضافة المؤسسة',
  'Organization updated successfully': 'تم تحديث المؤسسة بنجاح',
  'Failed to update organization': 'تعذر تحديث المؤسسة',
  'Organization deleted': 'تم حذف المؤسسة',
  'Failed to delete organization': 'تعذر حذف المؤسسة',
  'Failed to update organization status': 'تعذر تحديث حالة المؤسسة',
  'Failed to update key person. Please try again.': 'تعذر تحديث الشخص الرئيسي. يرجى المحاولة مرة أخرى.',

  // SuperAdminView — users
  'Users Management': 'إدارة المستخدمين',
  'Create User': 'إنشاء مستخدم',
  'Manage user details and organization access': 'إدارة بيانات المستخدم وصلاحيات الوصول إلى المؤسسات',
  'Password management is available in local account mode only.': 'إدارة كلمات المرور متاحة في وضع الحساب المحلي فقط.',
  'Nationality': 'الجنسية',
  'Select Nationality': 'اختر الجنسية',
  'Gender': 'الجنس',
  'digits missing': 'أرقام ناقصة',
  'Organization Membership': 'عضوية المؤسسة',
  'Select Organization': 'اختر المؤسسة',
  'Owner': 'المالك',
  'Admin': 'مسؤول',
  'Assign All Modules': 'إسناد جميع الوحدات',
  'Add Membership': 'إضافة عضوية',
  'Current Memberships': 'العضويات الحالية',
  'Full Name, Email, and at least one Organization membership are required': 'الاسم الكامل والبريد الإلكتروني وعضوية مؤسسة واحدة على الأقل مطلوبة',
  'Password must be at least 8 characters': 'يجب ألا تقل كلمة المرور عن 8 أحرف',
  'New password must be at least 8 characters': 'يجب ألا تقل كلمة المرور الجديدة عن 8 أحرف',
  'User updated successfully': 'تم تحديث المستخدم بنجاح',
  'User created with multiple memberships': 'تم إنشاء المستخدم بعضويات متعددة',
  'Failed to update user status': 'تعذر تحديث حالة المستخدم',
  'User removed': 'تمت إزالة المستخدم',
  'Failed to remove user': 'تعذرت إزالة المستخدم',

  // SuperAdminView — products and packages
  'Products & Services': 'المنتجات والخدمات',
  'Add Product/Service': 'إضافة منتج/خدمة',
  'Manage your offerings and pricing packages': 'إدارة عروضك وباقات التسعير',
  'Premium Subscription': 'اشتراك مميز',
  'Type': 'النوع',
  'Product': 'منتج',
  'Service': 'خدمة',
  'Describe the product or service...': 'صِف المنتج أو الخدمة...',
  'Packages': 'الباقات',
  'Add Package': 'إضافة باقة',
  'Package Name': 'اسم الباقة',
  'Basic Plan': 'الباقة الأساسية',
  'Price': 'السعر',
  'Package Description': 'وصف الباقة',
  "What's included in this package?": 'ما الذي تتضمنه هذه الباقة؟',
  'Packages & Pricing': 'الباقات والأسعار',
  'No packages defined for this product.': 'لم تُحدد باقات لهذا المنتج.',
  'Subscription Rate': 'سعر الاشتراك',
  'Product updated successfully': 'تم تحديث المنتج بنجاح',
  'Product added successfully': 'تمت إضافة المنتج بنجاح',
  'Failed to save product': 'تعذر حفظ المنتج',
  'Product deleted': 'تم حذف المنتج',
  'Failed to delete product': 'تعذر حذف المنتج',
  'Product assigned to organization': 'تم إسناد المنتج إلى المؤسسة',
  'Failed to assign product': 'تعذر إسناد المنتج',
  'Product unassigned from organization': 'تم إلغاء إسناد المنتج من المؤسسة',
  'Failed to unassign product': 'تعذر إلغاء إسناد المنتج',
  'Selecting an organization will link this product to its subscription profile.': 'سيؤدي اختيار مؤسسة إلى ربط هذا المنتج بملف اشتراكها.',
  'Assigned Organizations': 'المؤسسات المعيّنة',
  'No organizations currently assigned this product.': 'لا توجد مؤسسات معيّنة لهذا المنتج حاليًا.',
  '• Renew:': '• التجديد:',
  'Unassign from Product': 'إلغاء الإسناد من المنتج',

  // FinancialNotificationsChecker
  'Invoice Overdue': 'فاتورة متأخرة',
  'Invoice Due Today': 'فاتورة مستحقة اليوم',
  'Upcoming Invoice Payment': 'دفعة فاتورة قادمة',
  'Quote Expired': 'انتهت صلاحية عرض السعر',
  'Quote Expiring Today': 'ينتهي عرض السعر اليوم',
  'Upcoming Quote Expiry': 'انتهاء عرض سعر قريب',
  'Bill Overdue': 'فاتورة مشتريات متأخرة',
  'Bill Due Today': 'فاتورة مشتريات مستحقة اليوم',
  'Upcoming Bill Payment': 'دفعة فاتورة مشتريات قادمة',
  'PO Overdue': 'أمر شراء متأخر',
  'PO Delivery Today': 'تسليم أمر شراء اليوم',
  'Upcoming PO Delivery': 'تسليم أمر شراء قريب',
  'Could not load local financial documents': 'تعذر تحميل المستندات المالية المحلية',
  'Could not create local notification': 'تعذر إنشاء الإشعار المحلي',

  // AIChatBot
  'Organization AI': 'ذكاء المؤسسة',
  'Only:': 'للمؤسسة فقط:',
  'active organization': 'المؤسسة النشطة',
  'Open chat history': 'فتح سجل المحادثات',
  'Start new conversation': 'بدء محادثة جديدة',
  'Expand assistant': 'توسيع المساعد',
  'Minimize assistant': 'تصغير المساعد',
  'Close assistant': 'إغلاق المساعد',
  'Your chat history': 'سجل محادثاتك',
  'Private to you in': 'خاص بك داخل',
  'this organization': 'هذه المؤسسة',
  'New chat': 'محادثة جديدة',
  'Loading history…': 'جاري تحميل السجل…',
  'No saved conversations yet.': 'لا توجد محادثات محفوظة بعد.',
  'messages ·': 'رسائل ·',
  'Delete conversation': 'حذف المحادثة',
  'I can summarize, find records, calculate financial information, and create charts from': 'يمكنني التلخيص والعثور على السجلات وحساب المعلومات المالية وإنشاء الرسوم البيانية من بيانات',
  'the active organization': 'المؤسسة النشطة',
  'only.': 'فقط.',
  'You': 'أنت',
  'Reading organization data…': 'جاري قراءة بيانات المؤسسة…',
  'Ask organization AI': 'اسأل ذكاء المؤسسة',
  'Ask about revenue, invoices, stock…': 'اسأل عن الإيرادات أو الفواتير أو المخزون…',
  'Listening…': 'جاري الاستماع…',
  'Stop voice input': 'إيقاف الإدخال الصوتي',
  'Start voice input': 'بدء الإدخال الصوتي',
  'Type with your voice': 'اكتب باستخدام صوتك',
  'Send question': 'إرسال السؤال',
  'Data is filtered on the server by the active organization.': 'تُصفّى البيانات على الخادم وفق المؤسسة النشطة.',
  'Could not load chat history': 'تعذر تحميل سجل المحادثات',
  'Could not load conversation': 'تعذر تحميل المحادثة',
  'Could not load conversation history.': 'تعذر تحميل سجل المحادثة.',
  'Could not delete conversation': 'تعذر حذف المحادثة',
  'AI request failed': 'فشل طلب الذكاء الاصطناعي',
  'No answer was returned.': 'لم يتم إرجاع إجابة.',
  'The AI request failed. Please try again.': 'فشل طلب الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.',
};

export const hrSettingsAdminPatterns: Array<[RegExp, (...matches: string[]) => string]> = [
  // Preserve the captured organization, user, document, file, account, code,
  // status, date, amount, identifier, URL, and other runtime values verbatim.
  [/^Failed to load chart of accounts: (.+)$/i, status => `تعذر تحميل دليل الحسابات: ${status}`],
  [/^Failed to load branding themes: (.+)$/i, status => `تعذر تحميل سمات الهوية البصرية: ${status}`],
  [/^Failed to save branding theme: (.+)$/i, status => `تعذر حفظ سمة الهوية البصرية: ${status}`],
  [/^Failed to delete branding theme: (.+)$/i, status => `تعذر حذف سمة الهوية البصرية: ${status}`],
  [/^Failed to save organization settings \((.+)\)$/i, status => `تعذر حفظ إعدادات المؤسسة (${status})`],
  [/^Failed to load organization settings \((.+)\)$/i, status => `تعذر تحميل إعدادات المؤسسة (${status})`],
  [/^Profile update failed with status (.+)$/i, status => `فشل تحديث الملف الشخصي بالحالة ${status}`],
  [/^Failed to load employees: (.+)$/i, status => `تعذر تحميل الموظفين: ${status}`],
  [/^Failed to update employee: (.+)$/i, status => `تعذر تحديث الموظف: ${status}`],
  [/^Failed to create employee: (.+)$/i, status => `تعذر إنشاء الموظف: ${status}`],
  [/^Failed to delete employee: (.+)$/i, status => `تعذر حذف الموظف: ${status}`],
  [/^Request failed \((.+)\)$/i, status => `فشل الطلب (${status})`],
  [/^Uploading (.+)\.\.\.$/i, value => `جاري رفع ${value}...`],
  [/^(.+) sent$/i, value => `تم إرسال ${value}`],
  [/^Failed to upload (.+)$/i, value => `تعذر رفع ${value}`],
  [/^Successfully imported (\d+) employees$/i, count => `تم استيراد ${count} موظفًا بنجاح`],
  [/^Failed to import employee (.+) \((.+)\)$/i, (employee, status) => `تعذر استيراد الموظف ${employee} (${status})`],
  [/^We found (\d+) employees in your file\. Review them before importing\.$/i, count => `عثرنا على ${count} موظفًا في ملفك. راجع بياناتهم قبل الاستيراد.`],
  [/^Importing employees (\d+) of (\d+)$/i, (completed, total) => `جاري استيراد الموظفين ${completed} من ${total}`],
  [/^Import stopped after (\d+) of (\d+) employees: (.+)$/i, (completed, total, error) => `توقف الاستيراد بعد ${completed} من ${total} موظفًا: ${error}`],
  [/^Payrun marked as (.+)$/i, status => `تم تغيير حالة دورة الرواتب إلى ${status}`],
  [/^Started at (.+)$/i, time => `بدأت في ${time}`],
  [/^(\d+)h (\d+)m$/i, (hours, minutes) => `${hours} س ${minutes} د`],
  [/^(\d+)m$/i, minutes => `${minutes} د`],
  [/^Step (.+) of (.+)$/i, (step, total) => `الخطوة ${step} من ${total}`],
  [/^(\d+) replies$/i, count => `${count} ردود`],
  [/^(\d+) days$/i, count => `${count} أيام`],
  [/^Failed to load activity logs: (.+)$/i, status => `تعذر تحميل سجلات النشاط: ${status}`],
  [/^Permission (added|removed)$/i, action => action.toLowerCase() === 'added' ? 'تمت إضافة الصلاحية' : 'تمت إزالة الصلاحية'],
  [/^(\d+) Activated$/i, count => `${count} مفعّلة`],
  [/^(.+) mentioned you in a note on (.+)$/i, (person, entityType) => `${person === 'Someone' ? 'شخص ما' : person} أشار إليك في ملاحظة على ${({ invoice: 'فاتورة مبيعات', quote: 'عرض سعر', bill: 'فاتورة مشتريات', task: 'مهمة', contact: 'جهة اتصال' } as Record<string, string>)[entityType.toLowerCase()] || entityType}`],
  [/^By (.+) • (.+)$/i, (user, date) => `بواسطة ${user} • ${date}`],
  [/^(\d+) Threads Available$/i, count => `${count} مناقشة متاحة`],
  [/^Uploading (.+)…$/i, value => `جاري رفع ${value}…`],
  [/^Delete (.+)$/i, value => `حذف ${value}`],
  [/^(\d+) messages · (.+)$/i, (count, date) => `${count} رسائل · ${date}`],
  [/^Only: (.+)$/i, organization => `للمؤسسة فقط: ${organization}`],
  [/^Private to you in (.+)$/i, organization => `خاص بك داخل ${organization}`],
  [/^I can summarize, find records, calculate financial information, and create charts from (.+) only\.$/i, organization => `يمكنني التلخيص والعثور على السجلات وحساب المعلومات المالية وإنشاء الرسوم البيانية من بيانات ${organization} فقط.`],
  [/^License is (.+)$/i, status => `حالة الترخيص: ${status}`],
  [/^License status: (.+)$/i, status => `حالة الترخيص: ${status}`],
  [/^Device limit reached \((.+)\)\. Reset license rules in portal\.$/i, limit => `تم بلوغ حد الأجهزة (${limit}). أعد ضبط قواعد الترخيص في البوابة.`],
  [/^Unique Device ID:\s*(.+)$/i, id => `معرّف الجهاز الفريد: ${id}`],
  [/^Switched to (.+)$/i, organization => `تم التبديل إلى ${organization}`],
  [/^Search (.+)\.\.\.$/i, area => `ابحث في ${area}...`],
  [/^License (.+) successfully$/i, status => `تم تحديث الترخيص إلى ${status} بنجاح`],
  [/^(.+) suspended$/i, organization => `تم إيقاف ${organization}`],
  [/^(.+) activated$/i, organization => `تم تفعيل ${organization}`],
  [/^(.+) Offline Installer Downloaded!$/i, platform => `تم تنزيل مثبّت ${platform} غير المتصل!`],
  [/^Saved key ending in ••••(.+); leave blank to keep it$/i, suffix => `المفتاح المحفوظ ينتهي بـ ••••${suffix}؛ اترك الحقل فارغًا للاحتفاظ به`],
  [/^Action Required: Your Accountex Subscription for (.+) is Expiring Soon$/i, organization => `إجراء مطلوب: اشتراك Accountex الخاص بـ ${organization} سينتهي قريبًا`],
  [/^Success: Your Accountex Subscription for (.+) has been Renewed$/i, organization => `تم بنجاح: جُدد اشتراك Accountex الخاص بـ ${organization}`],
  [/^Dear (.+),$/i, recipient => `عزيزي/عزيزتي ${recipient}،`],
  [/^Invoice (.+) for (.+) is past due\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `الفاتورة ${number} الخاصة بـ ${contact} متأخرة. الإجمالي: ${currency} ${total}`],
  [/^Invoice (.+) for (.+) is due today\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `الفاتورة ${number} الخاصة بـ ${contact} مستحقة اليوم. الإجمالي: ${currency} ${total}`],
  [/^Invoice (.+) for (.+) is due soon\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `الفاتورة ${number} الخاصة بـ ${contact} ستستحق قريبًا. الإجمالي: ${currency} ${total}`],
  [/^Quote (.+) for (.+) has expired\.$/i, (number, contact) => `انتهت صلاحية عرض السعر ${number} الخاص بـ ${contact}.`],
  [/^Quote (.+) for (.+) expires today\.$/i, (number, contact) => `تنتهي صلاحية عرض السعر ${number} الخاص بـ ${contact} اليوم.`],
  [/^Quote (.+) for (.+) expires soon\.$/i, (number, contact) => `ستنتهي صلاحية عرض السعر ${number} الخاص بـ ${contact} قريبًا.`],
  [/^Bill (.+) from (.+) is overdue\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `فاتورة المشتريات ${number} من ${contact} متأخرة. الإجمالي: ${currency} ${total}`],
  [/^Bill (.+) from (.+) is due today\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `فاتورة المشتريات ${number} من ${contact} مستحقة اليوم. الإجمالي: ${currency} ${total}`],
  [/^Bill (.+) from (.+) is due soon\. Total: (.+) (.+)$/i, (number, contact, currency, total) => `فاتورة المشتريات ${number} من ${contact} ستستحق قريبًا. الإجمالي: ${currency} ${total}`],
  [/^Purchase Order (.+) from (.+) is overdue\.$/i, (number, contact) => `أمر الشراء ${number} من ${contact} متأخر.`],
  [/^Purchase Order (.+) from (.+) is scheduled for delivery today\.$/i, (number, contact) => `أمر الشراء ${number} من ${contact} مقرر تسليمه اليوم.`],
  [/^Purchase Order (.+) from (.+) is due for delivery soon\.$/i, (number, contact) => `أمر الشراء ${number} من ${contact} مقرر تسليمه قريبًا.`],
  [/^Total Staff:\s*(\d+)$/i, count => `إجمالي الموظفين: ${count}`],
  [/^Expires:\s*(.+)$/i, date => `ينتهي في: ${date}`],
  [/^Devices:\s*(.+)$/i, count => `الأجهزة: ${count}`],
  [/^Packages \((\d+)\)$/i, count => `الباقات (${count})`],
];
